import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  adminApi,
  AnalyticsSummary,
  AdminUserRow,
  JourneyEvent,
  JourneyRecord,
} from './adminApi';
import { EmailBody } from './AdminActivity';
import AdminDeletedCalls from './AdminDeletedCalls';
import AdminEmails, { AnswerBadge } from './AdminEmails';
import AdminFeedback from './AdminFeedback';
import AdminGrowth from './AdminGrowth';
import AdminPnl from './AdminPnl';
import { SUBSCRIPTION_EVENT_LABELS, SubscriptionSummary } from './AdminSubscriptions';
import { fmtAdminDateTime } from './timeFormat';
import './Admin.css';

type SortKey =
  | 'email'
  | 'subscriptionPlan'
  | 'creditsRemaining'
  | 'callCount'
  | 'creditsPurchased'
  | 'lastCallAt'
  | 'createdAt'
  | 'source';

interface ColumnDef {
  key: SortKey;
  label: string;
  type: 'string' | 'number' | 'date';
  value: (u: AdminUserRow) => string | number | null | undefined;
}

const COLUMNS: ColumnDef[] = [
  { key: 'email', label: 'Email', type: 'string', value: (u) => u.email || u.uid },
  { key: 'subscriptionPlan', label: 'Plan', type: 'string', value: (u) => u.subscriptionPlan },
  { key: 'creditsRemaining', label: 'Credits', type: 'number', value: (u) => u.creditsRemaining },
  { key: 'callCount', label: 'Calls', type: 'number', value: (u) => u.callCount },
  { key: 'creditsPurchased', label: 'Purchased', type: 'number', value: (u) => u.creditsPurchased },
  { key: 'lastCallAt', label: 'Last call', type: 'date', value: (u) => u.lastCallAt },
  { key: 'createdAt', label: 'Signed up', type: 'date', value: (u) => u.createdAt },
  {
    key: 'source',
    label: 'Source',
    type: 'string',
    value: (u) => u.attribution?.utmSource || u.attribution?.referrer,
  },
];

function Breakdown({ title, data }: { title: string; data: Record<string, number> }) {
  const entries = Object.entries(data).sort((a, b) => b[1] - a[1]);
  return (
    <div className="admin-breakdown">
      <h3>{title}</h3>
      {entries.length === 0 ? (
        <div className="admin-muted">No data</div>
      ) : (
        entries.map(([k, v]) => (
          <div className="row" key={k}>
            <span>{k}</span>
            <span>{v}</span>
          </div>
        ))
      )}
    </div>
  );
}

/** Readable names for events derived from purchases, subscriptions, emails
 * and surveys. */
const EVENT_LABELS: Record<string, string> = {
  signed_up: 'Signed up',
  purchase: 'Purchase',
  subscription_renewed: 'Subscription renewed',
  ...SUBSCRIPTION_EVENT_LABELS,
  email_received: 'Email received',
  email_failed: 'Email failed to send',
  email_sending: 'Email sending',
  email_answered: 'Answered from the email',
  questionnaire_submitted: 'Questionnaire submitted',
  nps_questionnaire_submitted: 'NPS questionnaire submitted',
};

function PayloadValue({ name, value }: { name: string; value: unknown }) {
  if (name === 'metNeeds') return <AnswerBadge value={String(value)} />;
  if (Array.isArray(value)) return <>{value.join(', ')}</>;
  if (value !== null && typeof value === 'object') {
    return <pre className="admin-prompt journey-long">{JSON.stringify(value, null, 2)}</pre>;
  }
  const text = String(value);
  // Long text (an email body, a comment) stays one line until opened.
  if (text.length > 140 || text.includes('\n')) {
    return (
      <details>
        <summary>{text.replace(/\s+/g, ' ').slice(0, 120)}…</summary>
        <pre className="admin-prompt journey-long">{text}</pre>
      </details>
    );
  }
  return <>{text}</>;
}

function EventPayload({ payload }: { payload: Record<string, any> }) {
  const entries = Object.entries(payload).filter(
    ([, v]) => v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0)
  );
  if (!entries.length) return null;
  return (
    <dl className="admin-kv journey-payload">
      {entries.map(([k, v]) => (
        <div key={k} style={{ display: 'contents' }}>
          <dt>{k}</dt>
          <dd>
            <PayloadValue name={k} value={v} />
          </dd>
        </div>
      ))}
    </dl>
  );
}

function EventDetails({ event }: { event: JourneyEvent }) {
  return (
    <>
      {event.unverified && (
        <div className="admin-muted" style={{ fontSize: 12 }}>
          <span className="answer-badge partial">unverified</span> Self-reported on the public form; anyone
          can submit it for any user.
        </div>
      )}
      {event.payload && <EventPayload payload={event.payload} />}
      {event.emailLogId && <EmailBody logId={event.emailLogId} />}
    </>
  );
}

function JourneyTimeline({ journey }: { journey: JourneyRecord }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="admin-call">
      <div className="admin-call-head">
        <div>
          <strong>{journey.email || journey.uid}</strong>
          <div className="admin-call-meta" style={{ marginTop: 4 }}>
            <span>{journey.subscriptionPlan || '—'}</span>
            <span>{journey.creditsRemaining ?? '—'} credits</span>
            <span>{journey.eventCount} events</span>
          </div>
        </div>
        <button className="admin-inline-btn" onClick={() => setExpanded((prev) => !prev)}>
          {expanded ? 'Hide timeline' : 'Show timeline'}
        </button>
      </div>

      {expanded && (
        <div className="journey-timeline" style={{ marginTop: 12 }}>
          {journey.timeline.map((event, index) => (
            <div className="journey-event" key={`${journey.uid}-${index}`}>
              <div className="journey-dot" />
              <div className="journey-content">
                <div className="journey-meta">
                  <strong title={event.eventName}>
                    {EVENT_LABELS[event.eventName || ''] || event.eventName}
                  </strong>
                  <span>{fmtAdminDateTime(event.timestamp)}</span>
                  {event.source && <span>{event.source}</span>}
                </div>
                {event.eventStage && <div className="admin-muted">stage: {event.eventStage}</div>}
                <EventDetails event={event} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

type Tab = 'analytics' | 'growth' | 'journeys' | 'pnl' | 'emails' | 'feedback' | 'deleted';

function AdminDashboard() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [journeys, setJourneys] = useState<JourneyRecord[]>([]);
  // The tab lives in the URL (?tab=emails) so it survives reloads and can be linked.
  const [params, setParams] = useSearchParams();
  // Survey answers used to be a section of the Emails tab; keep those links working.
  const activeTab =
    params.get('tab') === 'emails' && params.get('section') === 'feedback'
      ? 'feedback'
      : (params.get('tab') as Tab) || 'analytics';
  const setActiveTab = (tab: Tab) => setParams(tab === 'analytics' ? {} : { tab });
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({
    key: 'createdAt',
    dir: 'desc',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, u, j] = await Promise.all([
          adminApi.summary(),
          adminApi.users(),
          adminApi.journeys(),
        ]);
        if (cancelled) return;
        setSummary(s);
        setUsers(u.users);
        setJourneys(j.journeys);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        (u.email || '').toLowerCase().includes(q) ||
        (u.displayName || '').toLowerCase().includes(q) ||
        u.uid.toLowerCase().includes(q)
    );
  }, [users, search]);

  const sorted = useMemo(() => {
    const col = COLUMNS.find((c) => c.key === sort.key);
    if (!col) return filtered;
    const arr = [...filtered];
    arr.sort((a, b) => {
      const av = col.value(a);
      const bv = col.value(b);
      // Missing values always sort to the bottom, regardless of direction.
      const aEmpty = av === null || av === undefined || av === '';
      const bEmpty = bv === null || bv === undefined || bv === '';
      if (aEmpty && bEmpty) return 0;
      if (aEmpty) return 1;
      if (bEmpty) return -1;
      let cmp: number;
      if (col.type === 'string') {
        cmp = String(av).toLowerCase().localeCompare(String(bv).toLowerCase());
      } else if (col.type === 'date') {
        cmp = new Date(av as string).getTime() - new Date(bv as string).getTime();
      } else {
        cmp = (av as number) - (bv as number);
      }
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [filtered, sort]);

  const toggleSort = (key: SortKey) => {
    setSort((prev) => {
      if (prev.key === key) {
        return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      }
      const col = COLUMNS.find((c) => c.key === key);
      // Numbers/dates default to descending (highest / most recent first),
      // strings default to ascending (A–Z).
      return { key, dir: col && col.type === 'string' ? 'asc' : 'desc' };
    });
  };

  if (loading && !['growth', 'emails', 'feedback', 'pnl', 'deleted'].includes(activeTab)) {
    return <div className="admin-container">Loading analytics…</div>;
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>Admin</h1>
      </div>

      {error && <div className="admin-error">{error}</div>}

      <div className="admin-tabs" style={{ marginBottom: 20 }}>
        <button
          className={`admin-inline-btn ${activeTab === 'analytics' ? 'active' : ''}`}
          onClick={() => setActiveTab('analytics')}
        >
          Analytics
        </button>
        <button
          className={`admin-inline-btn ${activeTab === 'growth' ? 'active' : ''}`}
          onClick={() => setActiveTab('growth')}
        >
          Growth
        </button>
        <button
          className={`admin-inline-btn ${activeTab === 'journeys' ? 'active' : ''}`}
          onClick={() => setActiveTab('journeys')}
        >
          Journeys
        </button>
        <button
          className={`admin-inline-btn ${activeTab === 'pnl' ? 'active' : ''}`}
          onClick={() => setActiveTab('pnl')}
        >
          P&amp;L
        </button>
        <button
          className={`admin-inline-btn ${activeTab === 'emails' ? 'active' : ''}`}
          onClick={() => setActiveTab('emails')}
        >
          Emails
        </button>
        <button
          className={`admin-inline-btn ${activeTab === 'feedback' ? 'active' : ''}`}
          onClick={() => setActiveTab('feedback')}
        >
          Feedback
        </button>
        <button
          className={`admin-inline-btn ${activeTab === 'deleted' ? 'active' : ''}`}
          onClick={() => setActiveTab('deleted')}
        >
          Deleted calls
        </button>
      </div>

      {activeTab === 'analytics' && summary && (
        <>
          <div className="admin-cards">
            <div className="admin-card">
              <div className="value">{summary.totals.users}</div>
              <div className="label">Users</div>
            </div>
            <div className="admin-card">
              <div className="value">{summary.totals.calls}</div>
              <div className="label">Calls</div>
            </div>
            <div className="admin-card">
              <div className="value">{summary.totals.payments}</div>
              <div className="label">Payments (checkouts + renewals)</div>
            </div>
            <div className="admin-card">
              <div className="value">{summary.totals.creditsGranted}</div>
              <div className="label">Credits sold</div>
            </div>
            <div className="admin-card">
              <div className="value">{summary.totals.creditsOutstanding}</div>
              <div className="label">Credits outstanding</div>
            </div>
            <div className="admin-card">
              <div className="value">
                {Math.round(summary.totals.totalCallDurationSeconds / 60)}m
              </div>
              <div className="label">Total call time</div>
            </div>
          </div>

          {summary.subscriptions && <SubscriptionSummary metrics={summary.subscriptions} />}

          <div className="admin-breakdowns">
            <Breakdown title="Users by plan" data={summary.usersByPlan} />
            {summary.subscriptions && (
              <Breakdown title="Active subscribers by plan" data={summary.subscriptions.activeByPlan} />
            )}
            <Breakdown title="Calls by status" data={summary.callsByStatus} />
            <Breakdown title="Calls by source" data={summary.callsBySource} />
            <Breakdown title="Payments by plan (checkouts + renewals)" data={summary.paymentsByPlan} />
            {summary.subscriptions && (
              <Breakdown title="Paying users by plan" data={summary.subscriptions.payingUsersByPlan} />
            )}
            <Breakdown title="UTM sources" data={summary.attribution.utmSources} />
            <Breakdown title="UTM campaigns" data={summary.attribution.utmCampaigns} />
          </div>

          <div className="admin-header">
            <h1 style={{ fontSize: 18 }}>Users ({sorted.length})</h1>
            <input
              className="admin-search"
              placeholder="Search email, name, or UID…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  {COLUMNS.map((col) => (
                    <th
                      key={col.key}
                      className="admin-th-sortable"
                      onClick={() => toggleSort(col.key)}
                      aria-sort={
                        sort.key === col.key
                          ? sort.dir === 'asc'
                            ? 'ascending'
                            : 'descending'
                          : 'none'
                      }
                    >
                      {col.label}
                      <span className="admin-sort-arrow">
                        {sort.key === col.key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sorted.map((u) => (
                  <tr key={u.uid} onClick={() => navigate(`/admin/users/${u.uid}`)}>
                    <td>{u.email || <span className="admin-muted">{u.uid}</span>}</td>
                    <td>{u.subscriptionPlan || '—'}</td>
                    <td>{u.creditsRemaining ?? '—'}</td>
                    <td>{u.callCount}</td>
                    <td>{u.creditsPurchased}</td>
                    <td>{fmtAdminDateTime(u.lastCallAt)}</td>
                    <td>{fmtAdminDateTime(u.createdAt)}</td>
                    <td>{u.attribution?.utmSource || u.attribution?.referrer || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {activeTab === 'journeys' && (
        <div className="admin-section">
          <div className="admin-header">
            <h1 style={{ fontSize: 18 }}>Journeys ({journeys.length})</h1>
          </div>
          {journeys.length === 0 ? (
            <div className="admin-muted">No journey data</div>
          ) : (
            journeys.map((journey) => <JourneyTimeline key={journey.uid} journey={journey} />)
          )}
        </div>
      )}

      {activeTab === 'emails' && <AdminEmails />}
      {activeTab === 'feedback' && <AdminFeedback />}
      {activeTab === 'growth' && <AdminGrowth />}
      {activeTab === 'pnl' && <AdminPnl />}
      {activeTab === 'deleted' && <AdminDeletedCalls />}
    </div>
  );
}

export default AdminDashboard;
